/**
 * Tiny fetch wrapper that attaches the Cognito **ID token** as
 * `Authorization: Bearer <IdToken>` (REST API Cognito authorizer) at `VITE_API_BASE`.
 *
 * Surface is deliberately minimal — every business endpoint wrapper (`me.ts`,
 * and later `workouts.ts` etc.) calls `apiFetch(path, init)` and decodes the
 * JSON itself. No retry, no SWR, no caching at this layer; those land in
 * MVP 3+ where they actually matter.
 */

import { getIdToken } from '../auth/CognitoClient';

const DEFAULT_BASE = 'https://api-dev.fitness-v3.leonardomartins.com.au';

function getBase(): string {
  const v = import.meta.env.VITE_API_BASE;
  if (typeof v === 'string' && v.length > 0) return v.replace(/\/+$/, '');
  return DEFAULT_BASE;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface ApiFetchInit extends Omit<RequestInit, 'headers' | 'body'> {
  headers?: HeadersInit;
  body?: BodyInit | null | Record<string, unknown>;
  /**
   * When `false`, the call is made without the `Authorization` header.
   * Default `true`. Public endpoints (none in MVP 1) can opt out.
   */
  authenticated?: boolean;
}

export async function apiFetch<T = unknown>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const base = getBase();
  const url = path.startsWith('http') ? path : `${base}${path.startsWith('/') ? '' : '/'}${path}`;
  const headers = new Headers(init.headers ?? {});

  if (init.authenticated !== false) {
    const token = await getIdToken();
    if (!token) {
      throw new ApiError('Not signed in', 401, null);
    }
    headers.set('Authorization', `Bearer ${token}`);
  }

  let body: BodyInit | null | undefined;
  if (init.body && typeof init.body === 'object' && !(init.body instanceof FormData) && !(init.body instanceof Blob) && !(init.body instanceof ArrayBuffer)) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(init.body);
  } else {
    body = init.body as BodyInit | null | undefined;
  }

  const res = await fetch(url, { ...init, headers, body });
  const text = await res.text();
  let parsed: unknown = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    const rec = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    const message = [rec.message, rec.error]
      .map((x) => (typeof x === 'string' ? x : ''))
      .find(Boolean);
    const fallback = `HTTP ${res.status}`;
    throw new ApiError(message || fallback, res.status, parsed);
  }
  return parsed as T;
}
